import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FileText, MessageSquare, Target, Users } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  apiListCampaigns,
  apiListSuppliers,
  apiListTemplates,
  type Campaign,
  type FormTemplate,
  type SupplierListItem,
} from '@/lib/api'
import { useAgents } from '@/contexts/AgentsContext'
import {
  SEARCH_KINDS,
  searchEntries,
  toCampaignEntries,
  toConversationEntries,
  toSupplierEntries,
  toTemplateEntries,
  type SearchEntry,
  type SearchKind,
  type SearchTarget,
} from '@/lib/globalSearch'

export type { SearchTarget } from '@/lib/globalSearch'

interface GlobalCommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (target: SearchTarget) => void
}

const GROUPS: Record<SearchKind, { heading: string; icon: LucideIcon }> = {
  supplier: { heading: 'Fournisseurs', icon: Users },
  campaign: { heading: 'Campagnes', icon: Target },
  template: { heading: 'Templates', icon: FileText },
  conversation: { heading: 'Conversations', icon: MessageSquare },
}

const LIMIT = 5
const EMPTY_RESULTS: Record<SearchKind, SearchEntry[]> = {
  supplier: [],
  campaign: [],
  template: [],
  conversation: [],
}
const SERVER_DEBOUNCE_MS = 300

export function GlobalCommandPalette({ open, onOpenChange, onSelect }: GlobalCommandPaletteProps) {
  const { conversations, selectConversation } = useAgents()

  const [query, setQuery] = useState('')
  const [suppliers, setSuppliers] = useState<SupplierListItem[]>([])
  const [suppliersTotal, setSuppliersTotal] = useState(0)
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [templates, setTemplates] = useState<FormTemplate[]>([])
  const [loadFailed, setLoadFailed] = useState(false)
  const [loading, setLoading] = useState(false)
  const [serverSuppliers, setServerSuppliers] = useState<{ query: string; items: SupplierListItem[] }>({
    query: '',
    items: [],
  })

  const openRef = useRef(open)
  openRef.current = open
  const onOpenChangeRef = useRef(onOpenChange)
  onOpenChangeRef.current = onOpenChange
  const loadRequestRef = useRef(0)
  const serverRequestRef = useRef(0)

  // Raccourci global Cmd/Ctrl+K (même depuis un champ de saisie).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.repeat) return
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        onOpenChangeRef.current(!openRef.current)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  // Chargement à l'ouverture (stale-while-revalidate : on garde les anciennes données).
  useEffect(() => {
    if (!open) return
    const requestId = ++loadRequestRef.current
    setLoading(true)
    void (async () => {
      const [s, c, t] = await Promise.allSettled([
        apiListSuppliers({ pageSize: 1000, sortBy: 'name', sortOrder: 'asc' }),
        apiListCampaigns(),
        apiListTemplates(),
      ])
      if (requestId !== loadRequestRef.current) return
      if (s.status === 'fulfilled') {
        setSuppliers(s.value.data)
        setSuppliersTotal(s.value.total)
      }
      if (c.status === 'fulfilled') setCampaigns(c.value)
      if (t.status === 'fulfilled') setTemplates(t.value)
      setLoadFailed(
        s.status === 'rejected' || c.status === 'rejected' || t.status === 'rejected'
      )
      setLoading(false)
    })()
    return () => {
      loadRequestRef.current++
    }
  }, [open])

  // Réinitialise la requête à la fermeture.
  useEffect(() => {
    if (!open) {
      setQuery('')
      setServerSuppliers({ query: '', items: [] })
    }
  }, [open])

  const suppliersTruncated = suppliersTotal > suppliers.length

  // Complément serveur (liste locale tronquée uniquement).
  useEffect(() => {
    const trimmed = query.trim()
    if (!open || !suppliersTruncated || trimmed.length < 2) return
    const requestId = ++serverRequestRef.current
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const res = await apiListSuppliers({ search: trimmed, pageSize: 10 })
          if (requestId !== serverRequestRef.current) return
          setServerSuppliers({ query: trimmed, items: res.data })
        } catch (e: unknown) {
          console.error('[GlobalCommandPalette] Erreur recherche fournisseurs:', e)
        }
      })()
    }, SERVER_DEBOUNCE_MS)
    return () => {
      clearTimeout(timer)
      serverRequestRef.current++
    }
  }, [query, open, suppliersTruncated])

  // Un useMemo par source : un rafraîchissement des conversations ne renormalise pas les fournisseurs.
  const supplierEntries = useMemo(() => toSupplierEntries(suppliers), [suppliers])
  const campaignEntries = useMemo(() => toCampaignEntries(campaigns), [campaigns])
  const templateEntries = useMemo(() => toTemplateEntries(templates), [templates])
  const conversationEntries = useMemo(() => toConversationEntries(conversations), [conversations])
  const entries = useMemo<SearchEntry[]>(
    () => [...supplierEntries, ...campaignEntries, ...templateEntries, ...conversationEntries],
    [supplierEntries, campaignEntries, templateEntries, conversationEntries]
  )

  const serverEntries = useMemo(
    () => toSupplierEntries(serverSuppliers.items),
    [serverSuppliers.items]
  )

  const results = useMemo(() => {
    if (!open) return EMPTY_RESULTS
    const base = searchEntries(entries, query, { limitPerKind: LIMIT })
    const trimmed = query.trim()
    if (suppliersTruncated && trimmed.length >= 2 && serverSuppliers.query === trimmed) {
      const seen = new Set(base.supplier.map((e) => e.id))
      const extra = serverEntries.filter((e) => !seen.has(e.id))
      base.supplier = [...base.supplier, ...extra].slice(0, LIMIT)
    }
    return base
  }, [open, entries, query, suppliersTruncated, serverSuppliers.query, serverEntries])

  const showLoading = loading && suppliers.length + campaigns.length + templates.length === 0

  const handleSelect = useCallback(
    (entry: SearchEntry) => {
      if (entry.kind === 'conversation') void selectConversation(entry.id)
      onSelect({ kind: entry.kind, id: entry.id })
      onOpenChange(false)
      setQuery('')
    },
    [onSelect, onOpenChange, selectConversation]
  )

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) setQuery('')
      onOpenChange(next)
    },
    [onOpenChange]
  )

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="overflow-hidden p-0">
        <DialogTitle className="sr-only">Recherche globale</DialogTitle>
        <DialogDescription className="sr-only">
          Recherchez un fournisseur, une campagne, un template ou une conversation.
        </DialogDescription>
        <Command
          shouldFilter={false}
          loop
          className="**:data-[slot=command-input-wrapper]:h-12 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-2.5"
        >
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder="Rechercher…"
            aria-label="Rechercher un fournisseur, une campagne, un template ou une conversation"
            className="pr-8"
          />
          <CommandList>
            {showLoading && (
              <p role="status" className="px-3 py-2 text-sm text-muted-foreground">
                Chargement…
              </p>
            )}
            {query.trim() && !showLoading && (
              <CommandEmpty>{`Aucun résultat pour « ${query.trim()} ».`}</CommandEmpty>
            )}
            {SEARCH_KINDS.map((kind) => {
              const items = results[kind]
              if (items.length === 0) return null
              const { heading, icon: Icon } = GROUPS[kind]
              return (
                <CommandGroup key={kind} heading={heading}>
                  {items.map((entry) => (
                    <CommandItem
                      key={`${entry.kind}:${entry.id}`}
                      value={`${entry.kind}:${entry.id}`}
                      onSelect={() => handleSelect(entry)}
                    >
                      <Icon className="text-muted-foreground" />
                      <span className="truncate">{entry.label}</span>
                      {entry.secondary && (
                        <span className="ml-auto shrink-0 pl-2 text-xs text-muted-foreground">
                          {entry.secondary}
                        </span>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )
            })}
            {loadFailed && (
              <p className="px-3 py-2 text-xs text-muted-foreground">
                Certaines données n'ont pas pu être chargées.
              </p>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  )
}
